/**
 * Per-row login provider picker for an already-registered git server.
 *
 * Without it the only way to attach an OAuth app to a server registered
 * before that app existed was to register the host a second time, which
 * strands every PAT and cached project on the old row (#118). Choosing
 * "PAT only" detaches via `clear_login_provider`, because the PATCH endpoint
 * reads `login_provider_id: null` as "unchanged".
 */

import { type ReactElement } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gitApi } from '../../git/api/gitApi';
import { useAdminLoginProviders } from '../../logins/hooks/useAdminLoginProviders';
import type { GitServer, GitServerUpdateRequest } from '../../git/types/git.types';

function errorDetail(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
    }
  }
  return 'Failed to change login provider.';
}

export function GitServerLoginProviderSelect({
  server,
}: {
  server: GitServer;
}): ReactElement {
  const qc = useQueryClient();
  const { data: providers } = useAdminLoginProviders();

  // Same eligibility rule as registration (the main Sanctum login is never a
  // git credential), except the currently attached provider always stays
  // listed so the select reflects the row's real state even once disabled.
  const options = (providers ?? []).filter(
    (p) =>
      p.id === server.login_provider_id || (p.enabled && p.type !== 'main_session'),
  );

  const update = useMutation({
    mutationFn: (body: GitServerUpdateRequest) => gitApi.updateServer(server.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gitServers'] });
      void qc.invalidateQueries({ queryKey: ['gitCredentials'] });
    },
  });

  const onChange = (value: string): void => {
    update.mutate(value ? { login_provider_id: value } : { clear_login_provider: true });
  };

  return (
    <>
      <select
        aria-label={`Login provider for ${server.display_name}`}
        className="git-server-manager__select"
        value={server.login_provider_id ?? ''}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        disabled={update.isPending || providers === undefined}
      >
        <option value="">PAT only</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.enabled ? p.display_name : `${p.display_name} (disabled)`}
          </option>
        ))}
      </select>
      {update.isError && (
        <p className="git-server-manager__error" role="alert">
          {errorDetail(update.error)}
        </p>
      )}
    </>
  );
}
